/* eslint-disable react-hooks/exhaustive-deps */
/**
 * 사용자 상세 정보 컴포넌트
 * 사용자의 상세 정보를 조회하고 관리하는 컴포넌트입니다.
 * 기본 정보, 예약 정보, 방문자 정보 등을 표시하고 관리합니다.
 */

import _ from "lodash";
import moment from "moment";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ServiceTypes } from "src/api/adminMemo/adminmemo-types";
import { getContractManage, getContractOperationInfo } from "src/api/contract/contract-api";
import { ContractManage, ContractStep } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { deleteReservationAsync, getReservationListAsync } from "src/api/reservation/reservation-api";
import { addUsers, delUser, getUser, resendInviteMessageAsync, subscribeCalendar } from "src/api/user/user-api";
import { MemberStatus, UserDetailModel } from "src/api/user/user-types";
import { deleteVisitorByInviter, getVisitorsAsync } from "src/api/visitor/visitor-api";
import { BaseButton, BaseModal, BaseSelect, ContentsTitle } from "src/components";
import AdminMemoV3 from "src/components/AdminMemoV3/AdminMemoV3";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import GoToListButton from "src/components/GoToListButton";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import MetaTag from "src/components/layout/MetaTag";
import RegistrationInformation from "src/components/RegistrationInformation";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { findRepresentativeContract } from "src/pages/contract/hooks/useContractRenewal";

import PagePath from "src/pagePath.json";
import { useErrorModal } from "src/recoil/errorModal/hook";
import { useModal } from "src/recoil/modalState/hook";

import { formatPhoneNumber, ViewYmdFormat, YmdFormat } from "src/utils/common-util";
import { getContractDetailPath } from "src/utils/route-util";
import { getInvitationChannelLabel, INVITE_EXPIRE_DAYS_OPTIONS, INVITEABLE_CONTRACT_STEP } from "../constants";
import { deleteInChunks } from "./deleteInChunks";

/**
 * 사용자 상세 정보 컴포넌트
 * 사용자의 상세 정보를 조회하고 관리하는 메인 컴포넌트입니다.
 */
const UserDetail = () => {
  const { isAuthority } = usePartnerAuthority();
  const { openErrorModal } = useErrorModal();
  const { setBaseModal, setAbstractModalZ1 } = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const { memberId: contractMemberId } = useParams();

  const tabs = [
    {
      label: "기본정보",
      value: "basic",
      disabled: false,
    },
  ];

  const query = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        allowDots: true,
      }),
    [location],
  );

  // 현재 활성화 되어야하는 tab
  const activeTab = useMemo(() => {
    if (query?.tab) {
      return tabs.find((tab) => tab.value === query?.tab);
    }
    return tabs[0];
  }, [query]);

  /**
   * 방문자 조회 파라미터
   */
  const pv: any = {
    contractApplyNumber: query.contractApplyNumber,
    visitStatus: "VISIT_USE",
    searchStartTime: moment().format(YmdFormat.WITH_TIME_ZONE),
    searchEndTime: "9999-12-31T23:59:59.999+09:00",
    isDeletedVisitorDisplay: false,
  };

  /**
   * 예약 조회 파라미터
   */
  const pr: any = {
    contractId: "",
    statusCode: "ACCEPTED,ACKNOWLEDGED,INCOMPLETE",
    containsOrganizerMemberNo: "",
    greaterThanEqualsEnd: "",
  };

  /**
   * 상태 관리 변수들
   */
  const [isDelete, setIsDelete] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isDeleteReservations, setIsDeleteReservations] = useState(false);
  const [isDeleteInvitations, setIsDeleteInvitations] = useState(false);
  const [isDeleteReservations2, setIsDeleteReservations2] = useState(false);
  const [isDeleteInvitations2, setIsDeleteInvitations2] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  const [isMaster, setIsMaster] = useState(false);
  const [isNotMaster, setIsNotMaster] = useState(false);

  const [success, setSuccess] = useState("");

  const [user, setUser] = useState<UserDetailModel>();
  //계약명, 상품명, 계약 이용신청 진행 단계 상태
  const [contractManage, setContractManage] = useState<ContractManage>();

  const [memberStatus, setMemberStatus] = useState("");
  const [operation, setOperation] = useState<any>();
  const [contractStep, setContractStep] = useState<string>();
  const [contractId, setContractId] = useState<number>();
  /**
   * 예약 및 방문자 관련 상태
   */
  const [remainingSharedSpaceReservation, setRemainingSharedSpaceReservation] = useState<Number>(0);
  const [remainingVisitorInvitations, setRemainingVisitorInvitations] = useState<Number>(0);
  const [remainingSharedSpaceReservationList, setRemainingSharedSpaceReservationList] = useState<any>();
  const [remainingVisitorInvitationsList, setRemainingVisitorInvitationsList] = useState<any>();

  const [paramsVisitor, setParamsVisitor] = useState<any>();
  const [paramsReservation, setParamsReservation] = useState<any>();

  const [visitPath, setVisitPath] = useState("");
  const [reservationPath, setReservationPath] = useState("");

  /**
   * API 호출 함수들
   */
  const { executeAsync: getUserDetail } = useApiOperation(getUser);
  const { executeAsync: getVisitorList } = useApiOperation(getVisitorsAsync, { noErrorModal: true });
  const { executeAsync: getReservationList } = useApiOperation(getReservationListAsync, { noErrorModal: true });
  const { executeAsync: delVisiterByInviter } = useApiOperation(deleteVisitorByInviter);
  // 건별 에러 모달 대신 삭제 결과를 요약해 한 번만 알린다
  const { executeAsync: delReservation } = useApiOperation(deleteReservationAsync, { noErrorModal: true });
  const { executeAsync: dUser } = useApiOperation(delUser);

  const { executeAsync: activeCalendar } = useApiOperation(subscribeCalendar);

  const { executeAsync: getOperation } = useApiOperation(getContractOperationInfo);
  //유저 재초대
  const { executeAsync: reInviteUser } = useApiOperation(addUsers);
  //유저 초대 알림톡 재발송
  const { executeAsync: resendInviteMessage } = useApiOperation(resendInviteMessageAsync, { noErrorModal: true });

  //계약명, 상품명을 보여주기 위한 API
  const { executeAsync: getContractManageAsync } = useApiOperation(getContractManage);

  /**
   * 계약 운영 정보 조회 함수
   */
  const callOperation = useCallback(
    async (contractManageId: number) => {
      const res: any = await getOperation({ id: Number(contractManageId) });
      const length = res.data.data.content.productManage.clientSecretValue.length;
      res.data.data.content.productManage.clientSecretValueBlocked = "*".repeat(length);
      setOperation(res.data.data.content);
    },
    [getOperation],
  );

  /**
   * 회원의 공용공간 예약 목록 조회 함수
   */
  const getMemberReservationList = useCallback(
    async (params: any) => {
      console.log("params.containsOrganizerMemberNo", params.containsOrganizerMemberNo);
      if (!params.containsOrganizerMemberNo) {
        // containsOrganizerMemberNo 가 없으면 공용공간 예약 목록 조회를 막음
        return;
      }

      params.contractId = user?.contractMember.contractId;
      params.greaterThanEqualsEnd = moment().format(YmdFormat.WITH_TIME_ZONE);
      const response: any = await getReservationList(params);
      setRemainingSharedSpaceReservation(response.data.data.content.length);
      setRemainingSharedSpaceReservationList(response.data.data.content);
    },
    [getReservationList, user?.contractMember.contractId],
  );

  /**
   * 회원이 초대한 방문자 목록 조회 함수
   */
  const getMembersInvitationVisitors = useCallback(
    async (params: any) => {
      console.log("params.inviteeMemberNo", params.inviteeMemberNo);

      if (!params.inviteeMemberNo) {
        // inviteeMemberNo 가 없으면 초대 방문자 목록 조회를 막음
        return;
      }

      if (!params.contractId) {
        params.contractId = user?.contractMember.contractId;
      }
      const response: any = await getVisitorList(params);
      if (response.data.data.contractVisitor) {
        setRemainingVisitorInvitationsList(response.data.data.contractVisitor);
        setRemainingVisitorInvitations(response.data.data.contractVisitor.length || 0);
      }
    },
    [getVisitorList, user?.contractMember.contractId],
  );

  /**
   * 데이터 삭제 후 목록 갱신 함수
   */
  const callDelDatas = useCallback(
    _.debounce(async () => {
      if (paramsReservation.contractId) {
        await getMemberReservationList({ ...paramsReservation, greaterThanEqualsEnd: today });
      }
      if (user?.contractMember?.contractManageId) {
        await getMembersInvitationVisitors({ ...paramsVisitor, contractApplyNumber: query.contractApplyNumber });
      }
    }, 500),
    [paramsReservation, user, paramsVisitor],
  );

  /**
   * 현재 날짜 문자열
   */
  const today = useMemo(() => {
    return moment().format(YmdFormat.WITH_TIME_ZONE);
  }, []);

  /**
   * 사용자 상세 정보 조회 함수
   */
  const callDetail = useCallback(
    async (contractMemberId: number) => {
      const res = await getUserDetail({ contractMemberId });
      if (Number(res?.data?.data?.contractMember?.contractId) > 0) {
        callOperation(res?.data?.data?.contractMember?.contractId);
      }

      setParamsVisitor(pv);

      setParamsReservation({
        ...pr,
        contractId: res?.data?.data?.contractMember?.contractId!,
        containsOrganizerMemberNo: res?.data?.data?.contractMember?.memberNo!,
      });
      const reserPath =
        PagePath.reservation.list +
        "?keyword=" +
        res?.data?.data?.contractMember?.memberNo! +
        "&statusCode=ACCEPTED,ACKNOWLEDGED,INCOMPLETE" +
        "&greaterThanEqualsEnd=" +
        today +
        "&containsOrganizerMemberNo=" +
        res?.data?.data?.contractMember?.memberNo! +
        "&contractId=" +
        res?.data?.data?.contractMember?.contractId!;
      setReservationPath(reserPath);
      //
      setUser(res?.data?.data);

      if (res?.data?.data?.contractMember?.memberNo !== undefined && res?.data?.data?.contractMember?.memberNo !== "") {
        pv.inviteeMemberNo = res?.data?.data?.contractMember?.memberNo!;
        const path =
          PagePath.visitor.list +
          "?contractApplyNumber=" +
          query.contractApplyNumber! +
          "&searchType=INVITEE_MEMBER_NO&searchValue=" +
          pv.inviteeMemberNo +
          "&visitStatus=VISIT_USE&searchStartTime=" +
          today +
          "&searchEndTime=" +
          pv.searchEndTime;
        setVisitPath(path);
      }

      setMemberStatus(res?.data?.data?.contractMember?.memberStatus);
    },
    [getUserDetail, query.contractManageId, query.contractApplyNumber, callOperation, pv, pr, today],
  );

  const onClickUpdate = useCallback(
    async (active: boolean) => {
      try {
        await activeCalendar({
          contractId: Number(user?.ctMember.contractId),
          memberNo: String(user?.contractMember.memberNo),
          active: active,
        });
        setSuccess(active ? "캘린더 구독이 활성화 되었습니다." : "캘린더 구독이 비활성화 되었습니다.");
      } finally {
        callDetail(Number(contractMemberId));
      }
    },
    [activeCalendar, user?.ctMember?.contractId, user?.contractMember?.memberNo, callDetail, contractMemberId],
  );

  const onMasterToggle = useCallback(
    async (isAdmin: boolean | undefined) => {
      setIsMaster(false);
      setIsNotMaster(false);
      await dUser({
        contractMemberId: Number(contractMemberId),
        contractManageId: Number(user?.contractMember?.contractManageId),
        isAdmin: isAdmin,
        delete: false,
      });
      setSuccess("권한이 변경되었습니다.");
      setIsDeleted(true);
    },
    [contractMemberId, dUser, user?.contractMember?.contractManageId],
  );

  const onDelete = useCallback(async () => {
    await dUser({
      contractMemberId: Number(contractMemberId),
      contractManageId: Number(user?.contractMember?.contractManageId),
      delete: true,
    });
    setIsDelete(false);
    setSuccess("삭제 되었습니다.");
    setIsDeleted(true);
  }, [contractMemberId, dUser, user?.contractMember?.contractManageId]);

  const onDeleted = useCallback(() => {
    setIsDeleted(false);
    setIsEditable(false);
    callDetail(Number(contractMemberId));
  }, [callDetail, contractMemberId]);

  const delDisable = useMemo(() => {
    return Number(remainingSharedSpaceReservation) !== 0 || Number(remainingVisitorInvitations) !== 0;
  }, [remainingSharedSpaceReservation, remainingVisitorInvitations]);

  //잔여 공용공간 삭제 함수
  const deleteReservation = useCallback(async () => {
    const reservationIds = (remainingSharedSpaceReservationList ?? []).map((reservation: { id: string }) => reservation.id);

    // 20건씩 순차로 삭제하고, 실패분은 한 번만 다시 시도한다
    const { successCount, failed } = await deleteInChunks(reservationIds, (reservationId: string) => delReservation({ reservationId }));

    // 부분 성공이어도 갱신해야 잔여 건수가 맞는다
    if (successCount > 0) {
      callDelDatas();
    }

    if (failed.length > 0) {
      openErrorModal({
        statusCode: failed.find((item) => item.status !== undefined)?.status,
        errorMessage: "일부 예약을 삭제하지 못했습니다.",
        errorList: [`성공: ${successCount}건`, `실패: ${failed.length}건`],
        causes: failed.map((item) => item.cause),
      });
      return;
    }

    setIsDeleteReservations2(true);
  }, [callDelDatas, delReservation, openErrorModal, remainingSharedSpaceReservationList]);

  //잔여 방문자 초대 삭제 함수
  const deleteVisitors = useCallback(async () => {
    //promise.allSettled 사용하여 삭제 시 쪼개서 순회하기
    let chunk: any = [];
    for (let i = 0; i < remainingVisitorInvitationsList.length; i++) {
      chunk.push(remainingVisitorInvitationsList[i].contractVisitorId);
    }

    await delVisiterByInviter({ inviteeMemberNo: user?.contractMember.memberNo!, contractVisitorIds: chunk });
    setIsDeleteInvitations2(true);
  }, [delVisiterByInviter, remainingVisitorInvitationsList, user?.contractMember.memberNo]);

  // 잔여 공용공간 삭제
  const deleteReservations = useCallback(async () => {
    setIsDeleteReservations(false);
    await deleteReservation();
  }, [deleteReservation]);

  // 잔여 방문자 초대 삭제
  const deleteInvitations = useCallback(async () => {
    setIsDeleteInvitations(false);
    await deleteVisitors();
  }, [deleteVisitors]);

  //============useEffect 데이터 조회 =============//

  useEffect(() => {
    // 이용자 상세 정보 조회
    if (contractMemberId) {
      callDetail(Number(contractMemberId));
    }
  }, [contractMemberId]);

  useEffect(() => {
    // 계약 관리 정보 조회 계약명, 상품명을 보여주기 위함
    getContractManageAsync({ contractApplyNumber: String(query.contractApplyNumber) }).then((res) => {
      const data = res?.data?.data;
      setContractManage(data?.contractManage);

      // RCA18의 단건 contractId/contractStep은 하위호환 때문에 항상 원계약을 가리킨다.
      // 재계약이 있으면 신청/계약번호 링크가 종료된 계약으로 연결되고, 재초대/재발송도 원계약 상태로 막힌다.
      // 화면에 보이는 계약과 판정 대상이 같아야 하므로 대표 계약(이용중 우선, 없으면 최신 계약) 하나로 맞추고,
      // contracts가 없으면(BE 미배포 등) 기존처럼 단건 값을 쓴다
      const representativeContract = findRepresentativeContract(data?.contracts);
      setContractId(representativeContract ? Number(representativeContract.contractId) : data?.contractId);
      setContractStep(representativeContract?.contractStep ?? data?.contractStep);
    });
  }, [getContractManageAsync, query.contractApplyNumber]);

  useEffect(() => {
    if (isDelete || isDeleteInvitations2) {
      // 삭제 후 데이터 갱신. 예약 삭제는 deleteReservation 이 직접 갱신한다
      callDelDatas();
    }
  }, [callDelDatas, isDelete, isDeleteInvitations2]);

  return (
    <>
      <MetaTag subTitle={String(user?.contractMember.memberNo)} />
      <div>
        <ContentsTitle
          title="이용자 초대"
          tabs={tabs}
          activeTab={activeTab}
          detailInfo={[
            { title: "이용자 ID", value: contractMemberId! },
            // { title: "신청번호", value: String(query?.contractApplyNumber!) }, 아이디만 노출하는것으로 변경 신청번호는 기본정보에 노출
          ]}
        />
        <div className="contents-container__scroll">
          <div className="contents-container__wrap page-user-form">
            {activeTab!.value === "basic" && (
              <>
                <article className="contents-container__wrap-article">
                  <BaseSectionTitle title={"이용자 정보"} />

                  <div className="flex-row flex-start">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>회원번호</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <div>{user?.contractMember.memberNo ? <p>{user?.contractMember.memberNo}</p> : "-"}</div>
                      </div>
                    </section>

                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>휴대폰 번호</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <p className="flex-row flex-center-start">
                          {user?.ctMember.phoneNumber
                            ? formatPhoneNumber(user?.ctMember.phoneNumber)
                            : user?.contractMember.inviteMobileNumber
                            ? formatPhoneNumber(user?.contractMember.inviteMobileNumber)
                            : "-"}
                          {user?.ctMember?.isPhoneNumberVerified && <div className="ml4 ic_certificate"></div>}
                        </p>
                      </div>
                    </section>
                  </div>

                  <div className="flex-row flex-start">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>닉네임</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <div>
                          <p>{user?.ctMember?.nickname || "-"}</p>
                        </div>
                      </div>
                    </section>
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>닉네임(추가)</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <div>
                          <p>{user?.ctMember?.secondaryNickname || "-"}</p>
                        </div>
                      </div>
                    </section>
                  </div>
                  <div className="flex-row flex-start">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>상태</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <div className="flex-center-start flex-row">
                          <div
                            className={`${
                              (memberStatus === "MEMBER_INVITE" && "status A") ||
                              (memberStatus === "MEMBER_USE" && "status B") ||
                              (memberStatus === "MEMBER_DELETE" && "status D") ||
                              (memberStatus === "MEMBER_CLOSE" && "status D") ||
                              (memberStatus === "MEMBER_EXPIRE" && "status D")
                            }`}
                          >
                            {memberStatus === MemberStatus.MEMBER_INVITE && "초대"}
                            {memberStatus === MemberStatus.MEMBER_USE && "사용"}
                            {memberStatus === MemberStatus.MEMBER_DELETE && "삭제"}
                            {memberStatus === MemberStatus.MEMBER_CLOSE && "사용종료"}
                            {memberStatus === MemberStatus.MEMBER_EXPIRE && "초대만료"}
                          </div>
                          {isAuthority("w") && memberStatus === MemberStatus.MEMBER_EXPIRE && (
                            // 재초대 가능 여부 API로 제공 예정
                            // 만료상태만 재초대 가능
                            <BaseButton
                              title="재초대"
                              disabled={user?.contractMember.isInviteEnable === false}
                              onClick={() => {
                                if (
                                  // 계약 이용신청 진행 단계가 초대 가능한 단계인지 확인
                                  // 초대 가능한 단계가 아니면 재초대 불가능
                                  !INVITEABLE_CONTRACT_STEP.includes(contractStep as ContractStep)
                                ) {
                                  return setBaseModal({
                                    isOpen: true,
                                    title: "초대할 수 없는 계약 상태입니다.",
                                    btnRightTitle: "확인",
                                  });
                                }

                                setAbstractModalZ1({
                                  isOpen: true,
                                  size: "medium",
                                  children: (() => {
                                    const InviteExpireModal = () => {
                                      const [inviteExpireDays, setInviteExpireDays] = useState<number>(3);

                                      const retryInvite = async () => {
                                        try {
                                          const res = await reInviteUser({
                                            contractManageId: String(user?.contractMember?.contractManageId),
                                            contractMemberList: [
                                              {
                                                // 핸드폰 번호가 있으면 핸드폰 번호로 초대
                                                ...(user?.contractMember?.inviteMobileNumber && {
                                                  inviteMobileNumber: user?.contractMember?.inviteMobileNumber,
                                                }),
                                                // 이메일이 있으면 이메일로 초대
                                                ...(user?.contractMember?.inviteEmail && { inviteEmail: user?.contractMember?.inviteEmail }),
                                                inviteExpireDays: inviteExpireDays,
                                              },
                                            ],
                                          });

                                          // 이용자 상세 정보 재조회
                                          callDetail(Number(contractMemberId));
                                          setBaseModal({
                                            isOpen: true,
                                            title: "다시 초대 및 등록되었습니다.",
                                            btnLeftTitle: "보러가기",
                                            btnRightTitle: "확인",
                                            onClose: () => {
                                              navigate(
                                                PagePath.user.detail.replace(":memberId", String(res?.data?.data?.contractMemberList[0])) +
                                                  "?contractApplyNumber=" +
                                                  query.contractApplyNumber +
                                                  "&tab=basic",
                                              );
                                              setBaseModal({ isOpen: false });
                                            },
                                            onClick: () => {
                                              setBaseModal({ isOpen: false });
                                            },
                                          });
                                        } finally {
                                          setAbstractModalZ1({ isOpen: false });
                                        }
                                      };
                                      return (
                                        <>
                                          <section className="base-abstract-modal__title buttom-list-wrap">
                                            <h1>재초대</h1>
                                            <ul className="base-list">
                                              <li className="font14">신청한 초대 만료 기한으로 알림톡이 다시 발송됩니다.</li>
                                              <li className="font14">등록한 정보로 새로운 이용자 초대 및 등록됩니다.</li>
                                            </ul>
                                          </section>
                                          <section className="base-abstract-modal__contents px30">
                                            <div className="flex-row flex-start">
                                              <section className="contents-container__grid">
                                                <div className="contents-container__grid-index">
                                                  <p className="required">초대 만료 기한</p>
                                                </div>
                                                <div className="contents-container__grid-contents">
                                                  <BaseSelect
                                                    className="minmax200"
                                                    stateOptions={INVITE_EXPIRE_DAYS_OPTIONS}
                                                    setStateValue={setInviteExpireDays}
                                                    value={inviteExpireDays}
                                                    menuPlacement="auto"
                                                  />
                                                </div>
                                              </section>
                                            </div>
                                          </section>
                                          <section className="base-abstract-modal__btn-wrap">
                                            <BaseButton
                                              title={"취소"}
                                              className="color-white"
                                              onClick={() => setAbstractModalZ1({ isOpen: false })}
                                            />
                                            <BaseButton title={"확인"} onClick={retryInvite} />
                                          </section>
                                        </>
                                      );
                                    };
                                    return <InviteExpireModal />;
                                  })(),
                                });
                              }}
                              className="color-white ml10"
                            />
                          )}

                          {isAuthority("w") && memberStatus === MemberStatus.MEMBER_INVITE && (
                            // 재초대 가능 여부 API로 제공 예정
                            // 초대상태만 재발송 가능
                            <BaseButton
                              title="재발송"
                              onClick={() => {
                                if (
                                  // 계약 이용신청 진행 단계가 초대 가능한 단계인지 확인
                                  // 초대 가능한 단계가 아니면 재발송 불가능
                                  !INVITEABLE_CONTRACT_STEP.includes(contractStep as ContractStep)
                                ) {
                                  return setBaseModal({
                                    isOpen: true,
                                    title: "초대할 수 없는 계약 상태입니다.",
                                    btnRightTitle: "확인",
                                  });
                                }

                                setBaseModal({
                                  isOpen: true,
                                  title: "재발송하시겠습니까?",
                                  children: `초대 만료일은 그대로 유지되며, 알림만 재발송됩니다. (초대만료일 : ${moment(
                                    user?.contractMember?.inviteExpireDate,
                                  ).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM_SS)})`,
                                  btnLeftTitle: "취소",
                                  btnRightTitle: "확인",

                                  onClick: () => {
                                    resendInviteMessage({ contractMemberId: Number(contractMemberId) })
                                      .then((res) => res?.data?.data?.content?.isSendResult)
                                      .catch(() => false)
                                      .then((isSendResult) =>
                                        setBaseModal({
                                          isOpen: true,
                                          title: isSendResult ? "재발송이 완료되었습니다." : "재발송이 실패하였습니다.",
                                          btnRightTitle: "확인",
                                        }),
                                      );
                                  },
                                });
                              }}
                              className="color-white ml10"
                            />
                          )}
                        </div>
                      </div>
                    </section>
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>이메일</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <div>
                          <p className="flex-row flex-center-start">
                            {user?.ctMember?.email
                              ? user?.ctMember?.email
                              : user?.contractMember?.inviteEmail
                              ? user?.contractMember?.inviteEmail
                              : "-"}
                            {user?.ctMember?.isEmailVerified && <div className="ml4 ic_certificate"></div>}
                          </p>
                        </div>
                      </div>
                    </section>
                  </div>
                  <div className="flex-row flex-start">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>초대일시</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <p>{user?.contractMember?.inviteDate ? moment(user?.contractMember?.inviteDate).format(ViewYmdFormat.FULL) : "-"}</p>
                      </div>
                    </section>
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>초대 만료 기한</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <p>
                          {user?.contractMember?.inviteExpireDate ? moment(user?.contractMember?.inviteExpireDate).format(ViewYmdFormat.FULL) : "-"}
                        </p>
                      </div>
                    </section>
                  </div>

                  <div className="flex-row flex-start">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>인증일시</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <p>{user?.ctMember?.joinedDate ? moment(user?.ctMember?.joinedDate).format(ViewYmdFormat.FULL) : "-"}</p>
                      </div>
                    </section>

                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>권한</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        {isEditable === false ? (
                          <div>
                            <p>
                              {user?.contractMember?.isAdmin === false && user?.contractMember?.isContractor === false && "없음"}
                              {user?.contractMember?.isAdmin === false && user?.contractMember?.isContractor === true && "계약자"}
                              {user?.contractMember?.isAdmin === true && user?.contractMember?.isContractor === true && "계약자, 마스터"}
                              {user?.contractMember?.isAdmin === true && user?.contractMember?.isContractor === false && "마스터"}
                            </p>
                          </div>
                        ) : (
                          <div className="flex-center-start flex-row">
                            <span className="mr10">
                              <div className="flex-center-start flex-row">
                                <div>{user?.contractMember?.isAdmin === false && user?.contractMember?.isContractor === false && "없음"}</div>

                                <div className="flex-center">
                                  {user?.contractMember?.isAdmin === false && user?.contractMember?.isContractor === true && "계약자"}
                                </div>

                                <div className="flex-center">
                                  {user?.contractMember?.isAdmin === true && user?.contractMember?.isContractor === true && "계약자, 마스터"}
                                </div>

                                <div className="flex-center">
                                  {user?.contractMember?.isAdmin === true && user?.contractMember?.isContractor === false && "마스터"}
                                </div>
                                {user?.contractMember?.isContractor === false && (
                                  <BaseButton
                                    title={user?.contractMember?.isAdmin === false ? "마스터 지정" : "마스터 해제"}
                                    onClick={() => {
                                      if (user?.contractMember?.isAdmin === true) {
                                        setIsNotMaster(true);
                                      } else {
                                        setIsMaster(true);
                                      }
                                    }}
                                    className="ml10 color-white"
                                  />
                                )}
                              </div>
                            </span>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                  <div className="flex-row flex-start">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>상태변경일시</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <p>{user?.contractMember?.statusDate ? moment(user?.contractMember?.statusDate).format(ViewYmdFormat.FULL) : "-"}</p>
                      </div>
                    </section>
                  </div>

                  {operation && operation?.productManage?.isUseCalendar === true && (
                    <div>
                      <section className="contents-container__grid">
                        <div className="contents-container__grid-index">
                          <p>캘린더 연동</p>
                        </div>
                        <div className="contents-container__grid-contents">
                          {isEditable === false ? (
                            <div>
                              <p>{user?.ctMember?.isWatchingCalendar ? "연동" : "미연동"}</p>
                            </div>
                          ) : (
                            <div className="flex-center-start flex-row">
                              <span className="mr10">{user?.ctMember?.isWatchingCalendar ? "연동" : "미연동"}</span>

                              <BaseButton
                                title={user?.ctMember?.isWatchingCalendar ? "미연동" : "연동"}
                                onClick={() => (user?.ctMember?.isWatchingCalendar === false ? onClickUpdate(true) : onClickUpdate(false))}
                                className="color-white"
                              />
                            </div>
                          )}
                        </div>
                      </section>
                    </div>
                  )}
                </article>

                <article className="contents-container__wrap-article">
                  <BaseSectionTitle title={"초대 정보"} />
                  <div className="flex-row flex-start">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>계약관리 ID</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <div className="flex-row flex-center-start">
                          <div>
                            {/* 계약관리 ID 단독 표기이므로 대표 계약(이용중 우선, 없으면 최신) 상세로 링크한다 */}
                            <BaseNewTabLink
                              path={getContractDetailPath({
                                contractId: contractId?.toString() ?? "",
                              })}
                              value={contractManage?.contractManageId?.toString() || "-"}
                            />
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>계약명</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <p>{contractManage?.contractName || "-"}</p>
                      </div>
                    </section>
                  </div>
                  <div className="flex-row flex-start">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>상품명</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        {/* 상품명 (상품 ID:00000) - 상품 ID 선택 시 새창으로 상품 상세 이동 */}
                        <div className="flex-row flex-center-start">
                          <p>{contractManage?.spaceProductName || "-"}</p>
                          {contractManage?.spaceProductId && (
                            <>
                              <p className="ml4">(상품 ID:</p>
                              <BaseNewTabLink
                                path={PagePath.product.detail.replace(":id", String(contractManage.spaceProductId))}
                                value={String(contractManage.spaceProductId)}
                              />
                              <p>)</p>
                            </>
                          )}
                        </div>
                      </div>
                    </section>
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>이용중 인원</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <p>
                          {operation?.productManage?.memberNums !== undefined ? operation?.productManage?.memberNums : "-"}명 이용중(총{" "}
                          {operation?.productManage?.memberMaxNums !== undefined ? operation?.productManage?.memberMaxNums : "-"}명 가능)
                        </p>
                      </div>
                    </section>
                  </div>
                  <div className="flex-row flex-start"></div>
                  <div className="flex-row flex-start"></div>
                  <div className="flex-row flex-start">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>초대자 이메일</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <div className="flex-row flex-center-start">
                          {(() => {
                            if (user?.contractMember.invitationChannel === "CTRL_ROOM") {
                              return <span>{user?.inviteeInfo?.inviteeMemberId || "-"}</span>;
                            } else {
                              return <span>{user?.inviteeInfo.inviteeEmail || "-"}</span>;
                            }
                          })()}
                        </div>
                      </div>
                    </section>
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>초대자 회원번호</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <p>{user?.inviteeInfo?.inviteeMemberNo || "-"}</p>
                      </div>
                    </section>
                  </div>
                  <div className="flex-row flex-start">
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>초대 경로</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <p>{getInvitationChannelLabel(user?.contractMember.invitationChannel)}</p>
                      </div>
                    </section>
                  </div>
                </article>

                <RegistrationInformation data={user?.contractMember} />

                {isEditable === false && <AdminMemoV3 serviceId={Number(contractMemberId)} serviceType={ServiceTypes.SERVICE_INVITED_USER} />}
              </>
            )}
          </div>
        </div>
        <BaseModal
          title="이용자를 삭제하시겠습니까?"
          isOpen={isDelete}
          btnLeftTitle={"취소"}
          btnRightTitle={"삭제"}
          btnRightDisable={delDisable}
          onClose={() => setIsDelete(false)}
          onClick={onDelete}
          children={
            <div>
              <div>
                잔여 공용공간 예약 및 방문자 초대가 없는 경우
                <br /> 이용자를 삭제할 수 있습니다.
              </div>

              <div className="flex-center-between minmax290 mt20" style={{ backgroundColor: "#F2F4F6", height: 40, padding: 10 }}>
                <div>
                  잔여 공용공간 예약 :{" "}
                  {Number(remainingSharedSpaceReservation) > 0 ? (
                    <BaseNewTabLink path={reservationPath} value={remainingSharedSpaceReservation + "건"}></BaseNewTabLink>
                  ) : (
                    remainingSharedSpaceReservation + "건"
                  )}
                </div>
                <button
                  className="base-trash-btn color-gray size-20"
                  disabled={Number(remainingSharedSpaceReservation) === 0}
                  onClick={(e) => {
                    if (memberStatus === MemberStatus.MEMBER_INVITE) {
                      // 이용자가 초대 상태인 경우 삭제 불가
                      return;
                    }
                    e.preventDefault();
                    setIsDeleteReservations(true);
                  }}
                ></button>
              </div>

              <div className="flex-center-between minmax290 mt10" style={{ backgroundColor: "#F2F4F6", height: 40, padding: 10 }}>
                <div>
                  잔여 방문자 초대 :{" "}
                  {Number(remainingVisitorInvitations) > 0 ? (
                    <BaseNewTabLink path={visitPath} value={remainingVisitorInvitations + "건"}></BaseNewTabLink>
                  ) : (
                    remainingVisitorInvitations + "건"
                  )}
                </div>
                <button
                  className="base-trash-btn color-gray size-20"
                  disabled={Number(remainingVisitorInvitations) === 0}
                  onClick={(e) => {
                    if (memberStatus === MemberStatus.MEMBER_INVITE) {
                      // 이용자가 초대 상태인 경우 삭제 불가
                      return;
                    }
                    e.preventDefault();
                    setIsDeleteInvitations(true);
                  }}
                ></button>
              </div>
            </div>
          }
        />

        <BaseModal title={success} isOpen={isDeleted} btnRightTitle={"확인"} onClick={onDeleted} />

        {/* 공용공간 삭제 */}
        <BaseModal
          title="해당 이용자의 잔여 공용공간 예약을 모두 삭제하시겠습니까?"
          isOpen={isDeleteReservations}
          btnLeftTitle={"취소"}
          btnRightTitle={"삭제"}
          onClose={() => setIsDeleteReservations(false)}
          onClick={() => deleteReservations()}
        />

        {/* 방문자 초대 삭제 */}
        <BaseModal
          title="해당 이용자의 잔여 방문자 초대를 모두 삭제하시겠습니까?"
          isOpen={isDeleteInvitations}
          btnLeftTitle={"취소"}
          btnRightTitle={"삭제"}
          onClose={() => setIsDeleteInvitations(false)}
          onClick={() => deleteInvitations()}
        />

        <BaseModal
          title="해당 이용자의 잔여 공용공간 예약이 모두 삭제되었습니다."
          isOpen={isDeleteReservations2}
          btnRightTitle={"확인"}
          onClick={() => setIsDeleteReservations2(false)}
        />

        {/* 방문자 초대 삭제 */}
        <BaseModal
          title="해당 이용자의 잔여 방문자 초대가 모두 삭제되었습니다."
          isOpen={isDeleteInvitations2}
          btnRightTitle={"확인"}
          onClick={() => setIsDeleteInvitations2(false)}
        />

        <BaseModal
          title="마스터 지정하시겠습니까?"
          isOpen={isMaster}
          btnLeftTitle={"취소"}
          btnRightTitle={"확인"}
          onClose={() => setIsMaster(false)}
          onClick={() => onMasterToggle(true)}
        >
          <div>
            <p>해당 이용자는 마스터가 되면, 다른 이용자를 초대/삭제할 수 있는 권한을 가지게 됩니다.</p>
          </div>
        </BaseModal>
        <BaseModal
          title="마스터 해제하시겠습니까?"
          isOpen={isNotMaster}
          btnLeftTitle={"취소"}
          btnRightTitle={"확인"}
          onClose={() => setIsNotMaster(false)}
          onClick={() => onMasterToggle(false)}
        >
          <div>
            <p>해당 이용자는 즉시 다른 이용자를 초대/삭제할 수 있는 권한이 없어지게 됩니다.</p>
          </div>
        </BaseModal>

        <div className="contents-container__btn-wrap">
          <div className="left-area d-flex">{isEditable ? null : <GoToListButton title={"목록으로"} />}</div>
          <div className="right-area">
            {isAuthority("d") &&
              (((user?.contractMember?.isContractor !== true && memberStatus === MemberStatus.MEMBER_USE) ||
                memberStatus === MemberStatus.MEMBER_INVITE) &&
              !isEditable ? (
                <BaseButton title="삭제" onClick={() => setIsDelete(true)} className="color-white size-large mr5" />
              ) : (
                (memberStatus === MemberStatus.MEMBER_INVITE || memberStatus === MemberStatus.MEMBER_USE) && (
                  <div className="ml40 mr10">
                    <p className="contents-container__sub-title-info">{"계약자는 이용자 삭제가 되지 않습니다."}</p>
                  </div>
                )
              ))}
            {isAuthority("w") && memberStatus === MemberStatus.MEMBER_USE && isEditable === false && (
              <BaseButton title="수정" onClick={() => setIsEditable(true)} className="size-large" />
            )}
            {memberStatus === MemberStatus.MEMBER_USE && isEditable === true && (
              <>
                <BaseButton
                  title={"수정취소"}
                  className="color-white size-large mr10"
                  onClick={() => {
                    setBaseModal({
                      isOpen: true,
                      title: "현재 페이지에서 나가시겠습니까?",
                      children: (
                        <span>
                          입력 중인 내용이 있는 경우, <br /> 저장을 하지 않은 정보는 사라집니다.
                        </span>
                      ),
                      btnLeftTitle: "머무르기",
                      btnRightTitle: "나가기",
                      onClick: () => {
                        setIsEditable(false);
                        setBaseModal({ isOpen: false });
                      },
                      onClose: () => {
                        setBaseModal({ isOpen: false });
                      },
                    });
                  }}
                />
                <BaseButton title="저장" className="size-large ml10" onClick={() => setIsEditable(false)} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default UserDetail;
